import cn from 'classnames';
import { FC, ReactNode, useEffect, useState } from 'react';

import { InfoBannerClosableProps, InfoBannerVariant } from '@/shared/config';

import { Icon } from '../Icon/Icon';
import { IconNames } from '../Icon/Icon.types';

import styles from './InfoBanner.module.scss';

type InfoBannerBaseProps = {
  message: ReactNode;
  variant?: InfoBannerVariant;
  icon?: IconNames;
  linkText?: string;
  linkHref?: string;
  className?: string;
  testID?: string;
  onLinkClick?: () => void;
  onClose?: () => void;
};

type InfoBannerProps = InfoBannerBaseProps & InfoBannerClosableProps;

export const InfoBanner: FC<InfoBannerProps> = (props) => {
  const {
    message,
    variant = 'info',
    icon,
    linkText,
    linkHref,
    closable = true,
    storageKey,
    className,
    testID,
    onLinkClick,
    onClose,
  } = props;

  const [isVisible, setIsVisible] = useState(() => {
    if (!storageKey) {
      return true;
    }

    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored !== 'closed';
    } catch (error) {
      return true;
    }
  });

  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const shouldShowLink = linkText && (linkHref || onLinkClick);
  const fontSize = variant === 'sales' ? 'p4' : 'p3';

  useEffect(() => {
    if (!storageKey || isVisible) {
      return;
    }

    try {
      sessionStorage.setItem(storageKey, 'closed');
    } catch (error) {
      return;
    }
  }, [isVisible, storageKey]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={cn(styles.container, styles[variant], className)}
      data-testid={testID}
    >
      <div className={cn(styles.content, closable && styles.contentWithClose)}>
        {icon && <Icon className={styles.icon} name={icon} size={20} />}

        <span className={cn(fontSize, styles.message)}>{message}</span>

        {shouldShowLink && (
          <a
            href={linkHref}
            className={cn(fontSize, styles.link)}
            onClick={handleLinkClick}
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkText}
          </a>
        )}
      </div>

      {closable && (
        <Icon
          className={styles.closeButton}
          name="close"
          size={32}
          onClick={handleClose}
        />
      )}
    </div>
  );
};
